import { type ReactNode, useEffect, useMemo } from "react";
import { IntlProvider } from "react-intl";
import { loadCatalog } from "./catalogs";
import { DEFAULT_LOCALE, detectLocale, isRtlLocale, type Locale } from "./locales";

export type I18nProviderProps = {
  /** Override the auto-detected locale. Useful for tests and QA. */
  locale?: Locale;
  children: ReactNode;
};

export function I18nProvider({ locale, children }: I18nProviderProps) {
  const active: Locale = locale ?? detectLocale();
  const messages = useMemo(() => loadCatalog(active), [active]);

  // Flip <html dir> for RTL locales so the whole document mirrors.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = active;
    document.documentElement.dir = isRtlLocale(active) ? "rtl" : "ltr";
  }, [active]);

  return (
    <IntlProvider
      locale={active}
      defaultLocale={DEFAULT_LOCALE}
      messages={messages}
      // Silence missing-translation warnings in prod. The catalog loader
      // already falls back to English, so a missing key is handled.
      onError={() => {}}
    >
      {children}
    </IntlProvider>
  );
}
